<template lang="pug">
.reply-and-nonreply-other-users-accordion
  // nonreply accordion
  .nonreply-accordion(v-if="type == 0")
    b-card-header#nonreply-header.p-2(header-tag="header" role="tab" header-class="accordion-toggle" v-b-toggle="'nonreply-accordion'")
      span.toggle-text 未回答者一覧（{{ otherUsers.length }}件）
    b-collapse#nonreply-accordion(accordion="nonreply-accordion" role="tabpanel")
      b-card-body.p-2
        AccordionLoading(:isBusy="isBusy")
        MessageHistoryOtherUserList(v-show="!isBusy" v-model="otherUsers" :otherUserGroupTypeMap="otherUserGroupTypeMap")

  // reply accordion
  .reply-accordion(v-if="type == 1")
    b-card-header#reply-header.p-2(header-tag="header" role="tab" header-class="accordion-toggle" v-b-toggle="'reply-accordion'")
      span.toggle-text 回答者一覧（{{ otherUsers.length }}件）
    b-collapse#reply-accordion(accordion="reply-accordion" role="tabpanel")
      b-card-body.p-2
        AccordionLoading(:isBusy="isBusy")
        MessageHistoryOtherUserList(v-show="!isBusy" v-model="otherUsers" :otherUserGroupTypeMap="otherUserGroupTypeMap")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore } from "@/store/store";
import { MobileSendSafetyConfirmDetailToOtherUserState } from "@/store/MobileSendSafetyConfirmDetailToOtherUser";

import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import OtherUserGroupSendMessageReadMembersService, { OtherUserGroupSendMessageReadMemberModel } from "@/commons/services/OtherUserGroupSendMessageReadMembers";

import MessageHistoryOtherUserList from "@/mobile/components/lists/MessageHistoryOtherUserList.vue";
import AccordionLoading from "@/mobile/basics/AccordionLoading.vue";

/**
 * ### 安否確認送信(施設関係者)の回答/未回答の施設関係者リスト
 */
@Component({
  components: {
    AccordionLoading,
    MessageHistoryOtherUserList
  }
})
export default class ReplyListAccordion extends Vue {
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;
  /** 送信メッセージにひもづくメンバーサービス */
  @Inject("OtherUserGroupSendMessageReadMembersService") otherUserGroupSendMessageReadMembersService!: OtherUserGroupSendMessageReadMembersService;

  /** ### 安否確認詳細画面で必要な情報 */
  @Getter("mobileSendSafetyConfirmDetailToOtherUserState") state!: MobileSendSafetyConfirmDetailToOtherUserState;

  /** 表示対象(既読家族、未読家族) */
  @Prop({ required: true })
  type!: ReplyListAccordionType;

  /** 安否確認 施設関係者メッセージソートキー */
  otherUserMessagesSortKey!: string[];
  /** ローディングの表示可否 */
  isBusy: boolean = false;
  /** 表示対象家族 */
  otherUsers: Array<OtherUserGroupSendMessageReadMemberModel> = [];
  /** 施設関係者グループ名MAP */
  otherUserGroupTypeMap: Map<number, string> = new Map();

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.otherUserMessagesSortKey = this.state.otherUserMessagesSortKey as string[];
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypeMap: Map<number, string> = this.createOtherUserGroupTypesMap(otherUserGroupTypes);
    this.otherUserGroupTypeMap = otherUserGroupTypeMap;

    if (this.otherUserMessagesSortKey != undefined) {
      let otherUsers: Array<OtherUserGroupSendMessageReadMemberModel> = [];

      for (const sortKey of this.otherUserMessagesSortKey) {
        const splitedSortKey: string[] = sortKey.split("_");
        const datetime: number = Number(splitedSortKey[2]);
        const users: Array<OtherUserGroupSendMessageReadMemberModel> = await this.getOtherUsers(datetime);

        otherUsers = otherUsers.concat(users);
      }

      this.otherUsers = otherUsers;
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);
    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypesMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();
    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }
    return map;
  }

  /**
   * ### 確認または未確認の施設関係者を設定する
   *
   * - propで渡されている種別に応じて確認、未確認のどちらの施設関係者を返すか決める
   */
  public async getOtherUsers(datetime: number): Promise<Array<OtherUserGroupSendMessageReadMemberModel>> {
    const readedOtherUsers: Array<OtherUserGroupSendMessageReadMemberModel> = await this.getSendMessageReadOtherUsers(datetime);

    let otherUsers: Array<OtherUserGroupSendMessageReadMemberModel> = [];

    for (const readedOtherUser of readedOtherUsers) {
      const extOtherUser: OtherUserGroupSendMessageReadMemberModel = {
        ...readedOtherUser
      };

      if (this.type == ReplyListAccordionType.NONREPLY_OTHERUSER) {
        if (extOtherUser.readDatetime == undefined || extOtherUser.readDatetime == null) {
          otherUsers.push(extOtherUser);
        }
      } else if (this.type == ReplyListAccordionType.REPLY_OTHERUSER) {
        if (extOtherUser.readDatetime != undefined && extOtherUser.readDatetime != null) {
          otherUsers.push(extOtherUser);
        }
      } else {
        console.error("ReplyListAccordion::setOtherUsers() unknown otherUser", extOtherUser);
      }
    }

    return otherUsers;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく施設関係者を取得する
   */
  public async getSendMessageReadOtherUsers(datetime: number): Promise<Array<OtherUserGroupSendMessageReadMemberModel>> {
    const otherUsers: Array<OtherUserGroupSendMessageReadMemberModel> = await this.otherUserGroupSendMessageReadMembersService.get(userStore, datetime);

    return otherUsers;
  }
}

export enum ReplyListAccordionType {
  NONREPLY_OTHERUSER = 0,
  REPLY_OTHERUSER = 1
}
</script>
